import { create } from 'zustand';
import type {AuditAnnualFs, ExcelImportResult} from "@/services/auditService/auditFsAnnualService";
import { get as getAnnualFs } from '@/services/auditService/auditFsAnnualService';
import type { AuditBuildCompanyFSTreeItems } from '@/services/auditService/auditCompanyFSCategoryService';
import type { AuditAdjustmentHeader } from '@/services/auditService/auditAdjustmentService';
import { normalizeCode } from '@/utils/fsCodes';
import {
  inferRetainedEarningsUseStored,
  computeDerivedRetainedEarnings,
  plAfterTaxLastYearFromMap,
  resolveRetainedEarningsAutoSplit,
  hasRetainedEarningsComparativeLastYear,
  retainedEarningsLastYearComponentsFromMap,
  getRetainedProfitsStoredAmount,
} from '@/pages/components/fs-report/FsFormula';
import { resolvePlCotInputMode } from '@/utils/plCostOfSalesVisibility';
import { computeFsPlAfterTaxFromMaps } from '@/pages/components/fs-report/plFsMultiCompute';

 const LEGACY_SCHEDULE_CODE_AND_NAME_TO_CODE: Record<string, string> = {
   'NONE|BALANCE SHEET': 'FS-BS',
   'NONE|PROFIT & LOSS': 'FS-PL',
   'NONE|NON-CURRENT ASSETS': 'BS-NCA',
   'NONE|CURRENT ASSETS': 'BS-CA',
   'NONE|CURRENT LIABILITIES': 'BS-CL',
   'NONE|NET CURRENT LIABILITIES/ ASSETS': 'BS-NCA-RES',
   'NONE|NON-CURRENT LIABILITIES': 'BS-NCL',
   'NONE|NET LIABILITIES/ ASSETS': 'BS-NL-RES',
   'NONE|CAPITAL AND RESERVES': 'BS-EQ',
   'NONE|OTHER NON-CURRENT ASSETS': 'BS-NCA-ONA',
   'NONE|RETAINED PROFITS/ ACCUMULATED LOSSES': 'BS-EQ-AL',
   'NONE|PROFIT/ LOSS FOR THE YEAR/ PERIOD': 'BS-EQ-LFP',
   'NONE|DIVIDEND': 'BS-EQ-DIV',
   'NONE|TURNOVER': 'PL-TURN',
   'NONE|COST OF TURNOVER': 'PL-COT',
   'NONE|GROSS PROFIT/LOSS': 'PL-GP',
   'NONE|OTHER INCOME': 'PL-OI',
   'NONE|GROSS PROFIT/ LOSS AND OTHER INCOME': 'PL-GPOI',
   'NONE|ADMINISTRATIVE EXPENSES': 'PL-AE',
   'NONE|FINANCE COST': 'PL-FC',
   'NONE|PROFIT/ LOSS BEFORE TAX': 'PL-PBT',
   'NONE|TAXATION': 'PL-TAX',
   'NONE|PROFIT/ LOSS AFTER TAX': 'PL-PAT',
   'NONE|COST OF SALES': 'PL-COT-COS',
   'NONE|TAX REFUNDABLE': 'BS-CA-TAXR',
   'NONE|PAID NO DOCS': 'BS-CA-PND',
   'NONE|TAX PAYABLE': 'BS-CL-TAXP',
   'NONE|RECEIVED NO DOCS': 'BS-CL-RND',
   'NONE|AMORTIZATION OF INTANGIBLE ASSETS': 'PL-AE-AIA',
   'PROPERTY_PLANT_EQUIPMENT|PROPERTY, PLANT AND EQUIPMENT': 'BS-NCA-PPE',
   'INTANGIBLE_ASSETS|INTANGIBLE ASSETS': 'BS-NCA-IA',
   'INVESTMENT_IN_TAXI_CABS_AND_LICENSES|INVESTMENT IN TAXI CABS AND LICENSES': 'BS-NCA-ITL',
   'INVESTMENT_IN_SUBSIDIARY|INVESTMENT IN A SUBSIDIARY': 'BS-NCA-ISUB',
   'INVESTMENT_IN_ASSOCIATE|INVESTMENT IN AN ASSOCIATE': 'BS-NCA-IASS',
   'INVESTMENT_ON_TRUSTS|INVESTMENT ON TRUSTS': 'BS-CA-IOT',
   'INVENTORIES|INVENTORIES': 'BS-CA-INV',
   'OTHER_RECEIVABLES|OTHER RECEIVABLES': 'BS-CA-ORC',
   'DEPOSIT_PAID|DEPOSIT PAID': 'BS-CA-DPP',
   'PREPAYMENTS|PREPAYMENTS': 'BS-CA-PPM',
   'TRADE_RECEIVABLES|TRADE RECEIVABLES': 'BS-CA-TR',
   'DUE_FROM_IMMEDIATE_HOLDING_COMPANY|AMOUNT DUE FROM AN IMMEDIATE HOLDING COMPANY': 'BS-CA-DIMH',
   'DUE_FROM_TO_DIRECTORS|AMOUNT DUE FROM A DIRECTOR': 'BS-CA-DIR',
   'DUE_FROM_SOLE_SHAREHOLDER|AMOUNT DUE FROM THE SOLE SHAREHOLDER': 'BS-CA-DSS',
   'DUE_FROM_RELATED_PARTIES|AMOUNT DUE FROM RELATED PARTIES': 'BS-CA-DRP',
   'DUE_FROM_RELATED_PARTIES_2|AMOUNT DUE FROM RELATED PARTIES 2': 'BS-CA-DRP2',
   'DUE_FROM_RELATED_PARTIES_3|AMOUNT DUE FROM RELATED PARTIES 3': 'BS-CA-DRP3',
   'BANK_BALANCE_AND_CASH|BANK BALANCE AND CASH': 'BS-CA-CASH',
   'TAX_PAYABLE|TAX PAYABLE': 'BS-CL-TAXP',
   'TRADE_PAYABLES|TRADE PAYABLES': 'BS-CL-TP',
   'OTHER_PAYABLES|OTHER PAYABLES': 'BS-CL-OP',
   'DIVIDEND_PAYABLE|DIVIDEND PAYABLE|DIVIDEND PAYABLES': 'BS-CL-DP',
   'DEPOSIT_RECEIVED|DEPOSIT RECEIVED': 'BS-CL-DR',
   'ACCRUED_EXPENSES|ACCRUED EXPENSES': 'BS-CL-ACC',
   'BANK_OVER_DRAFT|BANK OVER DRAFT': 'BS-CL-BOD',
   'BANK_OVER_DRAFT|BANK OVERDRAFT': 'BS-CL-BOD',
   'BANK_LOAN|BANK LOAN, SECURED': 'BS-CL-BLS',
   'VEHICLE_LOAN|OBLIGATION UNDER FINANCE LEASE': 'BS-CL-OFL',
   'DUE_TO_SUBSIDIARY|AMOUNT DUE TO A SUBSIDIARY': 'BS-CL-DTS',
   'DUE_TO_SHAREHOLDER|AMOUNT DUE TO A SHAREHOLDER': 'BS-CL-DTSH',
   'DUE_TO_RELATED_COMPANY|AMOUNT DUE TO RELATED COMPANY': 'BS-CL-DTRC',
   'DUE_TO_RELATED_PARTIES|AMOUNT DUE TO RELATED PARTIES': 'BS-CL-DRP',
   'DUE_TO_RELATED_PARTIES_2|AMOUNT DUE TO RELATED PARTIES 2': 'BS-CL-DRP2',
   'DUE_TO_RELATED_PARTIES_3|AMOUNT DUE TO RELATED PARTIES 3': 'BS-CL-DRP3',
   'DUE_FROM_TO_DIRECTORS|AMOUNT DUE TO A DIRECTOR': 'BS-CL-DTD',
   'OTHER_NON_CURRENT_ITEMS|OTHER NON-CURRENT ITEMS': 'BS-NCL-ONI',
   'NON_BANK_LOAN|BANK LOAN, SECURED': 'BS-NCL-BLS',
   'NON_VEHICLE_LOAN|OBLIGATION UNDER FINANCE LEASE': 'BS-NCL-OFL',
   'ISSUED_CAPITAL|ISSUED CAPITAL': 'BS-EQ-IC',
   'OTHER_FUND_FROM_SHAREHOLDER|OTHER FUND FROM A SHAREHOLDER': 'BS-EQ-OFFS',

   'SALES_INCOME|SALES INCOME': 'PL-TURN-SI',
   'SERVICE_INCOME|SERVICE INCOME': 'PL-TURN-SE',
   'RENTAL_INCOME|RENTAL INCOME': 'PL-TURN-RI',
   'OPENING_INVENTORIES|OPENING INVENTORIES': 'PL-COT-OI',
   'PURCHASES|PURCHASES': 'PL-COT-PUR',
   'CLOSING_INVENTORIES|LESS: CLOSING INVENTORIES': 'PL-COT-CI',
   'ROYALTY_EXPENSES|ROYALITY EXPENSES': 'PL-COT-ROY',
   'SUBCONTRACTOR_CHARGES|SUBCONTRACTOR CHARGES': 'PL-COT-SUB',
   'OTHER_COST_OF_TURNOVER|OTHER COST OF TURNOVER': 'PL-COT-OCT',
   'INTEREST_INCOME|INTEREST INCOME': 'PL-OI-II',
   'SUBSIDIES_FROM_GOVERNMENT|SUBSIDIES FROM GOVERNMENT': 'PL-OI-SFG',
   'DIVIDEND_INCOME|DIVIDEND INCOME': 'PL-OI-DI',
   'GAIN_LOSS_EXCHANGE|EXCHANGE GAIN': 'PL-OI-EXG',
   'ALLOWANCE_INCOME|ALLOWANCE INCOME': 'PL-OI-ALI',
   'GAIN_LOSS_DISPOSAL|GAIN ON DISPOSAL': 'PL-OI-GOD',
   'SUNDRY_INCOME|SUNDRY INCOME': 'PL-OI-SI',
   'ACCOUNTING_FEE|ACCOUNTING FEE': 'PL-AE-ACF',
   'ADMISSION_FEE|ADMISSION FEE': 'PL-AE-ADM',
   'ADVERTISING_AND_PROMOTION|ADVERTISING AND PROMOTION': 'PL-AE-ADP',
   'ANNUAL_RETURN|ANNUAL RETURN': 'PL-AE-AR',
   'AUDIT_AND_OTHER_PROFESSIONAL_FEE|AUDIT AND OTHER PROFESSIONAL FEE': 'PL-AE-AOP',
   'BANK_CHARGES|BANK CHARGES': 'PL-AE-BC',
   'BUILDING_MANAGEMENT_FEE|BUILDING MANAGEMENT FEE': 'PL-AE-BMF',
   'BUSINESS_REGISTRATION_FEE|BUSINESS REGISTRATION FEE': 'PL-AE-BRF',
   'BAD_DEBT|BAD DEBT': 'PL-AE-BD',
   'PROVISION_FOR_DOUBTFUL_DEBT|PROVISION FOR DOUBTFUL DEBT': 'PL-AE-PDD',
   'CERTIFICATION_FEE|CERTIFICATION FEE': 'PL-AE-CER',
   'CLEANING_EXPENSES|CLEANING EXPENSES': 'PL-AE-CLE',
   'COMPUTER_EXPENSES|COMPUTER EXPENSE': 'PL-AE-CMP',
   'COMPUTER_SYSTEM_EXPENSES|COMPUTER SYSTEM EXPENSES': 'PL-AE-CSE',
   'COMMISSION_PAYMENT|COMMISSION PAYMENTS': 'PL-AE-COM',
   'CONSULTANCY_FEE|CONSULTANCY FEE': 'PL-AE-CF',
   'CONSUMABLE|CONSUMABLE': 'PL-AE-CONS',
   'COURIER_AND_POSTAGE|COURIER AND POSTAGE': 'PL-AE-CUR',
   'CROWN_RENT_AND_RATES|CROWN RENT AND RATES': 'PL-AE-CRR',
   'CUSTOMS_AND_DECLARATION|CUSTOMS & DECLARATION': 'PL-AE-CUD',
   'DESIGN_FEE|DESIGN FEE': 'PL-AE-DSN',
   'DEPRECIATION|DEPRECIATION': 'PL-AE-DEP',
   'SALARIES_ALLOWANCE|DIRECTOR`S REMUNERATION': 'PL-AE-DR',
   'SALARIES_ALLOWANCE|DIRECTOR`S QUARTERS': 'PL-AE-DQ',
   'DONATION|DONATION': 'PL-AE-DON',
   'ELECTRICITY_AND_WATER|ELECTRICITY AND WATER': 'PL-AE-EW',
   'ENTERTAINMENT|ENTERTAINMENT': 'PL-AE-ENT',
   'GAIN_LOSS_EXCHANGE|EXCHANGE LOSS': 'PL-AE-EXL',
   'EXHIBITION_EXPENSE|EXHIBITION EXPENSE': 'PL-AE-EXH',
   'FRANCHISE|FRANCHISE': 'PL-AE-FRA',
   'FREIGHT_CHARGES|FREIGHT CHARGES': 'PL-AE-FRG',
   'GAS_EXPENSES|GAS EXPENSES': 'PL-AE-GAS',
   'HOTEL_EXPENSE|HOTEL EXPENSE': 'PL-AE-HOT',
   'INSURANCE|INSURANCE': 'PL-AE-INS',
   'INTELLECTUAL_PROPERTY_PAYMENTS|INTELLECTUAL PROPERTY PAYMENTS': 'PL-AE-IPP',
   'LEGAL_AND_PROFESSIONAL_FEE|LEGAL AND PROFESSIONAL FEE': 'PL-AE-LPF',
   'LICENSE_FEE|LICENSE FEE': 'PL-AE-LIC',
   'LOCAL_TRAVELLING|LOCAL TRAVELLING': 'PL-AE-LTR',
   'GAIN_LOSS_DISPOSAL|LOSS ON DISPOSAL': 'PL-AE-LOD',
   'MANAGEMENT_FEE|MANAGEMENT FEES': 'PL-AE-MGT',
   'MEDICAL_EXPENSES|MEDICAL EXPENSES': 'PL-AE-MED',
   'MEMBERSHIP_FEE|MEMBERSHIP FEE': 'PL-AE-MEM',
   'MOTOR_VEHICLE_EXPENSES|MOTOR VEHICLE EXPENSES': 'PL-AE-MVE',
   'SALARIES_ALLOWANCE|MPF CONTRIBUTIONS': 'PL-AE-MPF',
   'MESSING_EXPENSES|MESSING EXPENSES': 'PL-AE-MES',
   'OFFICE_EXPENSE|OFFICE EXPENSE': 'PL-AE-OFF',
   'OVERSEAS_TRAVELLING|OVERSEAS TRAVELLING': 'PL-AE-OTR',
   'PENALTY|PENALTY': 'PL-AE-PEN',
   'PRELIMINARY_EXPENSES|PRELIMINARY EXPENSES': 'PL-AE-PE',
   'PRINTING_AND_STATIONERY|PRINTING AND STATIONERY': 'PL-AE-PS',
   'PROPERTY_AGENCY_FEES|PROPERTY AGENCY FEES': 'PL-AE-PAF',
   'RENTAL_EXPENSE|RENTAL EXPENSE': 'PL-AE-REN',
   'REPAIR_AND_MAINTENANCE|REPAIR AND MAINTENANCE': 'PL-AE-RAM',
   'SALARIES_ALLOWANCE|SALARIES ALLOWANCE': 'PL-AE-SAL',
   'SAMPLE_FEE|SAMPLE FEE': 'PL-AE-SAM',
   'SECRETARIAL_FEE|SECRETARIAL FEE': 'PL-AE-SCF',
   'STAFF_WELFARE|STAFF WELFARE': 'PL-AE-SWF',
   'STAMP_DUTIES|STAMP DUTIES': 'PL-AE-SD',
   'STORAGE_FEE|STORAGE FEE': 'PL-AE-STO',
   'SUBSCRIPTION_FEE|SUBSCRIPTION FEE': 'PL-AE-SUB',
   'SUBCONTRACTING_EXPENSES|SUBCONTRACTING EXPENSES': 'PL-AE-SUE',
   'SUNDRY_EXPENSE|SUNDRY EXPENSE': 'PL-AE-SUN',
   'TELEPHONE_AND_INTERNET|TELEPHONE & INTERNET': 'PL-AE-TEL',
   'TRAINING_EXPENSE|TRAINNING EXPENSE': 'PL-AE-TRN',
   'TRAVELLING_EXPENSES|TRAVELLING EXPENSES': 'PL-AE-TRV',
   'TRANSPORTATION|TRANSPORTATION': 'PL-AE-TP',
   'UTILITIES_EXPENSES|UTILITIES EXPENSES': 'PL-AE-UTL',
   'VIRTUAL_OFFICE|VIRTUAL OFFICE': 'PL-AE-VO',
   'WAGES_EXPENSE|WAGES EXPENSE': 'PL-AE-WAG',
   'WEB_SERVICES_AND_EMAIL|WEB SERVICES AND EMAIL': 'PL-AE-WSE',
   'WITHHOLDING_TAX|WITHHOLDING TAX': 'PL-AE-WHT',
   'WITHHOLDING_TAX|WITHHOLD TAX': 'PL-AE-WHT',
   'BANK_LOAN|LOAN INTEREST EXPENSES': 'PL-FC-LI',
   'INTEREST|INTEREST': 'PL-FC-INT',
   'FIANCE_BANK_CHARGES|BANK CHARGES': 'PL-FC-BC',
   'IMPAIRMENT_LOSSES|IMPAIRMENT LOSSES': 'PL-AE-IML',
 };

 const LEGACY_SCHEDULE_CODE_AND_NAME_TO_CODE_LOOSE: Record<string, string> = (() => {
   const out: Record<string, string> = {};
   for (const [k, v] of Object.entries(LEGACY_SCHEDULE_CODE_AND_NAME_TO_CODE)) {
     const idx = k.indexOf('|');
     if (idx <= 0) continue;
     const sc = k.slice(0, idx);
     const nm = k.slice(idx + 1);
     const looseName = normalizeNameKeyLoose(nm);
     out[`${sc}|${looseName}`] = v;
   }
   return out;
 })();

 const LEGACY_SCHEDULE_CODE_TO_CODE: Record<string, string> = {
   'INVESTMENT_IN_SUBSIDIARY': 'BS-NCA-ISUB',
   'INVESTMENT_IN_ASSOCIATE': 'BS-NCA-IASS',
   'INVESTMENT_ON_TRUSTS': 'BS-CA-IOT',
 };

 function normalizeNameKeyStrict(input?: string | null): string {
   return normalizeCode(input)
     .replace(/\u2019/g, "'")
     .replace(/`/g, "'");
 }

 function normalizeNameKeyLoose(input?: string | null): string {
   return normalizeNameKeyStrict(input).replace(/[^A-Z0-9]+/g, '');
 }

 function deriveLeafCode(raw: any): string {
   const rawCode = normalizeCode(raw?.code);
   // 如果 code 已经是标准 FS 编码（BS-/PL-/FS-），直接返回
   if (rawCode && (rawCode.startsWith('FS-') || rawCode.startsWith('BS-') || rawCode.startsWith('PL-'))) return rawCode;

   const sc = normalizeCode(raw?.scheduleCode);
   if (!sc || sc === 'NONE') {
     // 无 scheduleCode：如果有 rawCode 则返回它，否则返回空
     return rawCode || '';
   }

   // If scheduleCode already looks like a FS code, treat it as code.
   if (sc.startsWith('FS-') || sc.startsWith('BS-') || sc.startsWith('PL-')) return sc;

   const nameKey = normalizeNameKeyStrict(raw?.nameEn ?? raw?.name ?? '');
   const mappedByName = LEGACY_SCHEDULE_CODE_AND_NAME_TO_CODE[`${sc}|${nameKey}`];
   if (mappedByName) return mappedByName;

   const nameKeyLoose = normalizeNameKeyLoose(nameKey);
   const mappedByLoose = LEGACY_SCHEDULE_CODE_AND_NAME_TO_CODE_LOOSE[`${sc}|${nameKeyLoose}`];
   if (mappedByLoose) return mappedByLoose;

   // Known legacy scheduleCode -> code mapping.
   const mapped = LEGACY_SCHEDULE_CODE_TO_CODE[sc];
   if (mapped) return mapped;

   // 如果 rawCode 存在（非标准前缀），使用它
   if (rawCode) return rawCode;

   // Fallback: use scheduleCode itself as code (but warn so we can gradually eliminate legacy values).
   // This avoids breaking lookups when backend hasn't provided a code yet.
   console.warn('[fsCategoryStore] Missing code; fallback to scheduleCode as code.', {
     fsId: raw?.fsId,
     name: raw?.name,
     scheduleCode: raw?.scheduleCode,
   });
   return sc;
 }

function plCotInputModeStorageKey(companyId: number, annualId: number): string {
  return `fs:plCotInputMode:${companyId}:${annualId}`;
}

function readStoredPlCotInputMode(companyId: number, annualId: number): 'COS' | 'PUR' | null {
  try {
    const stored = localStorage.getItem(plCotInputModeStorageKey(companyId, annualId));
    if (stored === 'COS' || stored === 'PUR') return stored;
  } catch {
  }
  return null;
}

function writeStoredPlCotInputMode(companyId: number | null | undefined, annualId: number | null | undefined, mode: 'COS' | 'PUR'): void {
  if (!companyId || !annualId) return;
  try {
    localStorage.setItem(plCotInputModeStorageKey(companyId, annualId), mode);
  } catch {
  }
}

export interface FsCategoryLine {
  fsId: number;
  name?: string | null;
  nameEn: string | null;
  aliasEn: string | null;
  nameZh: string | null;
  aliasZh: string | null;
  statement: 'BS' | 'PL' | string;
  amount: number | null;
  finalAmount: number | null;
  scheduleName: string | null;
  code: string;
  scheduleCode: string;
  entry?: string | null;
  wpStatus?: number | null;
  wpIssueRemark?: string | null;
  // Optional fields that may exist from API
  Level?: number | string | null;
  [k: string]: any;
}

interface FsCategoryState {
  // key: code -> FsCategoryLine
  byCode: Record<string, FsCategoryLine>;
  // cached BS/PL trees from getAnnualFs response
  bsTree?: any[];
  plTree?: any[];

  // Merged from fsEditingStore
  companyId: number | null;
  companyName: string | null;
  bsValues: Record<number, string>;
  plValues: Record<number, string>;
  /** Phase 3 MULTI: per-YOA PL leaf amounts. Key = yearOfAssessment. */
  plValuesByYoa: Record<string, Record<number, string>>;
  bsLastYearValues: Record<number, string>;
  plLastYearValues: Record<number, string>;
  bsHasLastYearChangeAudited: Set<number>;
  plHasLastYearChangeAudited: Set<number>;
  plAfterTax: number | null;
  plAfterTaxLastYear: number | null;
  plRoundedAfterTax: number | null;
  plPeriodType: 'year' | 'period' | null;
  afterTaxClientFromPL: number | null;
  plAdjustmentsByRefNo: Record<string, number>;
  adjustments: AuditAdjustmentHeader[] | null;
  allowedScheduleCodes?: Set<string>;
  scheduleNameMap?: Map<string, string>;
  firstYearAudit?: boolean;
  loading?: boolean;
  error?: string | null;
  _reqVersion?: number;
  loadedCompanyId?: number | null;
  loadedAnnualId?: number | null;
  /** BS-EQ-AL FS split override: true → parents/adj use stored (incl. 0); false → use derived roll-forward */
  bsRetainedEarningsUseStored: boolean;
  plCotInputMode: 'COS' | 'PUR';
  /** GL FS Summary：上年 PL 已滚入 BS-EQ-AL 时，BS LFP 上年不展示 */
  glSuppressPlAfterTaxLastYear: boolean;

  // Original methods
  setFsTree: (detail: AuditAnnualFs | any) => void;
  getLineByCode: (code: string) => FsCategoryLine | null;
  getBsTree: () => any[] | undefined;
  getPlTree: () => any[] | undefined;
  clear: () => void;

  // Merged methods from fsEditingStore
  setContext: (p: Partial<Pick<FsCategoryState, 'companyId' | 'companyName'>>) => void;
  setBSTree: (t: AuditBuildCompanyFSTreeItems[] | null) => void;
  setPLTree: (t: AuditBuildCompanyFSTreeItems[] | null) => void;
  setBsValues: (m: Record<number, string>) => void;
  setPlValues: (m: Record<number, string>) => void;
  setPlValuesByYoa: (m: Record<string, Record<number, string>>) => void;
  setBsLastYearValues: (m: Record<number, string>) => void;
  setPlLastYearValues: (m: Record<number, string>) => void;
  setBsRetainedEarningsUseStored: (v: boolean) => void;
  setPLComputed: (p: { afterTax?: number | null; afterTaxLastYear?: number | null; periodType?: 'year' | 'period' | null; clientAfterTax?: number | null }) => void;
  /**
   * Batch PL mount sync (values + lastYear + afterTax) in one set() to avoid
   * N store notifications while ProfitLoss initializes.
   */
  applyPlFsMountSync: (p: {
    plValues?: Record<number, string>;
    plLastYearValues?: Record<number, string>;
    afterTax?: number | null;
    afterTaxLastYear?: number | null;
    periodType?: 'year' | 'period' | null;
  }) => void;
  /** Recompute PL-PAT into store without mounting ProfitLoss (FS validation). */
  recomputePlAfterTax: () => void;
  setPlRoundedAfterTax: (v: number | null) => void;
  setPlAdjustmentsByRefNo: (m: Record<string, number>) => void;
  setPlCotInputMode: (mode: 'COS' | 'PUR') => void;
  setGlSuppressPlAfterTaxLastYear: (v: boolean) => void;
  setAdjustments: (p: Partial<Pick<FsCategoryState, 'adjustments' | 'allowedScheduleCodes' | 'scheduleNameMap'>>) => void;
  initIfNeeded: (companyId: number, annualId: number, importToken?: string | null) => Promise<void>;
  cancelInFlight: () => void;
  reset: () => void;
  applyImportResult: (result: ExcelImportResult) => void;
  /** Patch display name / aliases on byCode + trees after FsAdjustment alias edit */
  patchLineAlias: (code: string, patch: {
    name?: string | null;
    aliasEn?: string | null;
    aliasZh?: string | null;
  }) => void;
}

export const useFsCategoryStore = create<FsCategoryState>((set, get) => ({
  byCode: {},
  bsTree: undefined,
  plTree: undefined,
  
  // Merged initial state from fsEditingStore
  companyId: null,
  companyName: null,
  bsValues: {},
  plValues: {},
  plValuesByYoa: {},
  bsLastYearValues: {},
  plLastYearValues: {},
  bsHasLastYearChangeAudited: new Set(),
  plHasLastYearChangeAudited: new Set(),
  plAfterTax: null,
  plAfterTaxLastYear: null,
  plRoundedAfterTax: null,
  plPeriodType: null,
  afterTaxClientFromPL: null,
  plAdjustmentsByRefNo: {},
  adjustments: null,
  allowedScheduleCodes: undefined,
  scheduleNameMap: undefined,
  firstYearAudit: false,
  loading: false,
  error: null,
  _reqVersion: 0,
  loadedCompanyId: null,
  loadedAnnualId: null,
  bsRetainedEarningsUseStored: false,
  plCotInputMode: 'COS',
  glSuppressPlAfterTaxLastYear: false,

  clear: () => set({ 
    byCode: {}, 
    bsTree: undefined, 
    plTree: undefined,
    companyId: null,
    companyName: null,
    bsValues: {},
    plValues: {},
    plValuesByYoa: {},
    bsLastYearValues: {},
    plLastYearValues: {},
    plAfterTax: null,
    plRoundedAfterTax: null,
    plPeriodType: null,
    afterTaxClientFromPL: null,
    plAdjustmentsByRefNo: {},
    adjustments: null,
    allowedScheduleCodes: undefined,
    scheduleNameMap: undefined,
    firstYearAudit: false,
    loading: false,
    error: null,
    _reqVersion: 0,
    loadedCompanyId: null,
    loadedAnnualId: null,
    bsRetainedEarningsUseStored: false,
    plCotInputMode: 'COS',
    glSuppressPlAfterTaxLastYear: false,
  }),

  setFsTree: (detail) =>
    set(() => {
      const anyDetail: any = detail as any;
      // 新格式：直接从 balanceSheet.children 和 profitLoss.children 获取
      const bsLinesTree = anyDetail?.balanceSheet?.children as any[] | undefined;
      const plLinesTree = anyDetail?.profitLoss?.children as any[] | undefined;

      const next: Record<string, FsCategoryLine> = {};

      const upsert = (raw: any) => {
        const sc = String(raw?.scheduleCode ?? '').trim();
        const code = deriveLeafCode(raw);
        const fsIdNum = Number(raw?.fsId ?? 0) || 0;
        if (!code) {
          console.warn('[fsCategoryStore] Leaf node missing code; skipped indexing.', {
            fsId: raw?.fsId,
            nameEn: raw?.nameEn,
            name: raw?.name,
            scheduleCode: raw?.scheduleCode,
            level: raw?.level,
          });
          return;
        }
        const item: FsCategoryLine = {
          fsId: fsIdNum,
          name: raw?.name ?? null,
          nameEn: raw?.nameEn ?? null,
          aliasEn: raw?.aliasEn ?? null,
          nameZh: raw?.nameZh ?? null,
          aliasZh: raw?.aliasZh ?? null,
          statement: raw?.statement ?? '',
          amount: raw?.openingAmount ?? raw?.amount ?? null,
          finalAmount: raw?.finalAmount ?? null,
          scheduleName: raw?.scheduleName ?? null,
          code,
          scheduleCode: sc,
          entry: raw?.entry ?? null,  // 
          Level: raw?.Level ?? null,
          ...raw,
        } as FsCategoryLine;
        next[code] = item;
      };

      const traverse = (node: any) => {
        if (!node) return;
        const children = Array.isArray(node?.children) ? node.children : [];
        const lvl = node?.level;
        // level >= 3：常规叶子/父级科目；level 2 且无子节点：金额行（如 PL-TAX Taxation）
        if (Number(lvl) >= 3 || (Number(lvl) === 2 && children.length === 0)) {
          upsert(node);
        }
        for (const ch of children) traverse(ch);
      };

      const roots: any[] = [];
      if (Array.isArray(bsLinesTree)) roots.push(...bsLinesTree);
      if (Array.isArray(plLinesTree)) roots.push(...plLinesTree);
      for (const raw of roots) traverse(raw);

      return { byCode: next, bsTree: bsLinesTree, plTree: plLinesTree };
    }),

  getLineByCode: (code) => {
    const c = normalizeCode(code);
    if (!c) return null;
    return get().byCode[c] ?? null;
  },

  getBsTree: () => get().bsTree,
  getPlTree: () => get().plTree,

  // Merged methods from fsEditingStore
  setContext: (p) => set(p),
  setBSTree: (t) => set({ bsTree: t as any }),
  setPLTree: (t) => set({ plTree: t as any }),
  setBsValues: (m) => {
    const prev = get().bsValues || {};
    const next = m || {};
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (
      prevKeys.length === nextKeys.length
      && nextKeys.every((k) => prev[Number(k)] === next[Number(k)])
    ) {
      return;
    }
    set({ bsValues: next });
    get().recomputePlAfterTax();
  },
  setPlValues: (m) => {
    const prev = get().plValues || {};
    const next = m || {};
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    // 同内容短路：避免 init/COT sync 每次 new object 触发订阅方重跑（GL Last Year 爆栈）
    if (
      prevKeys.length === nextKeys.length
      && nextKeys.every((k) => prev[Number(k)] === next[Number(k)])
    ) {
      return;
    }
    set({ plValues: next });
    get().recomputePlAfterTax();
  },
  setPlValuesByYoa: (m) => {
    const prev = get().plValuesByYoa || {};
    const next = m || {};
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length === nextKeys.length) {
      let same = true;
      for (const yoa of nextKeys) {
        const a = prev[yoa] || {};
        const b = next[yoa] || {};
        const ak = Object.keys(a);
        const bk = Object.keys(b);
        if (ak.length !== bk.length || !bk.every((k) => a[Number(k)] === b[Number(k)])) {
          same = false;
          break;
        }
      }
      if (same) return;
    }
    set({ plValuesByYoa: next });
    get().recomputePlAfterTax();
  },
  setBsLastYearValues: (m) => {
    const prev = get().bsLastYearValues || {};
    const next = m || {};
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (
      prevKeys.length === nextKeys.length
      && nextKeys.every((k) => prev[Number(k)] === next[Number(k)])
    ) {
      return;
    }
    set({ bsLastYearValues: next });
    get().recomputePlAfterTax();
  },
  setPlLastYearValues: (m) => {
    const prev = get().plLastYearValues || {};
    const next = m || {};
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (
      prevKeys.length === nextKeys.length
      && nextKeys.every((k) => prev[Number(k)] === next[Number(k)])
    ) {
      return;
    }
    set({ plLastYearValues: next });
    get().recomputePlAfterTax();
  },
  setBsRetainedEarningsUseStored: (v) => set({ bsRetainedEarningsUseStored: v }),
  setPLComputed: ({ afterTax, afterTaxLastYear, periodType, clientAfterTax }) =>
    set((s) => {
      const nextAfterTax = afterTax ?? s.plAfterTax;
      const nextAfterTaxLastYear = afterTaxLastYear ?? s.plAfterTaxLastYear;
      const nextPeriodType = (periodType ?? s.plPeriodType) as any;
      const nextClient = clientAfterTax ?? s.afterTaxClientFromPL;
      if (
        nextAfterTax === s.plAfterTax
        && nextAfterTaxLastYear === s.plAfterTaxLastYear
        && nextPeriodType === s.plPeriodType
        && nextClient === s.afterTaxClientFromPL
      ) {
        return s;
      }
      return {
        plAfterTax: nextAfterTax,
        plAfterTaxLastYear: nextAfterTaxLastYear,
        plPeriodType: nextPeriodType,
        afterTaxClientFromPL: nextClient,
      };
    }),
  applyPlFsMountSync: ({ plValues, plLastYearValues, afterTax, afterTaxLastYear, periodType }) => {
    const s = get();
    const next: Partial<FsCategoryState> = {};
    let changed = false;
    if (plValues && plValues !== s.plValues) {
      next.plValues = plValues;
      changed = true;
    }
    if (plLastYearValues && plLastYearValues !== s.plLastYearValues) {
      // shallow-equal skip when same keys/values (mount re-entry)
      const prev = s.plLastYearValues || {};
      let lyChanged = Object.keys(plLastYearValues).length !== Object.keys(prev).length;
      if (!lyChanged) {
        for (const [k, v] of Object.entries(plLastYearValues)) {
          if (prev[Number(k)] !== v) {
            lyChanged = true;
            break;
          }
        }
      }
      if (lyChanged) {
        next.plLastYearValues = plLastYearValues;
        changed = true;
      }
    }
    if (afterTax !== undefined && afterTax !== s.plAfterTax) {
      next.plAfterTax = afterTax;
      changed = true;
    }
    if (afterTaxLastYear !== undefined && afterTaxLastYear !== s.plAfterTaxLastYear) {
      next.plAfterTaxLastYear = afterTaxLastYear;
      changed = true;
    }
    if (periodType !== undefined && periodType !== s.plPeriodType) {
      next.plPeriodType = periodType as any;
      changed = true;
    }
    if (!changed) return;
    set(next);
  },
  recomputePlAfterTax: () => {
    const s = get();
    if (!s.plTree || !Array.isArray(s.plTree) || s.plTree.length === 0) return;
    try {
      const { afterTax, afterTaxLastYear } = computeFsPlAfterTaxFromMaps({
        plTree: s.plTree,
        bsTree: s.bsTree,
        plValues: s.plValues || {},
        plValuesByYoa: s.plValuesByYoa,
        plLastYearValues: s.plLastYearValues || {},
        bsValues: s.bsValues || {},
        bsLastYearValues: s.bsLastYearValues || {},
        cotInputMode: s.plCotInputMode ?? 'COS',
      });
      if (s.plAfterTax === afterTax && s.plAfterTaxLastYear === afterTaxLastYear) return;
      set({ plAfterTax: afterTax, plAfterTaxLastYear: afterTaxLastYear });
    } catch {
      // keep previous
    }
  },
  setPlRoundedAfterTax: (v) => set({ plRoundedAfterTax: v }),
  setPlAdjustmentsByRefNo: (m) => set({ plAdjustmentsByRefNo: m }),
  setPlCotInputMode: (mode) => {
    set({ plCotInputMode: mode });
    const { loadedCompanyId, loadedAnnualId } = get();
    writeStoredPlCotInputMode(loadedCompanyId, loadedAnnualId, mode);
    get().recomputePlAfterTax();
  },
  setGlSuppressPlAfterTaxLastYear: (v) => set({ glSuppressPlAfterTaxLastYear: v }),
  setAdjustments: (p) => set(p),
  cancelInFlight: () => set((s) => ({ _reqVersion: (s._reqVersion || 0) + 1 })),
  
  initIfNeeded: async (companyId: number, annualId: number, importToken?: string | null) => {
    // fast path: already loaded for this key - optimized with stricter checks
    const state = get();
    const keyMatches = state.loadedCompanyId === companyId && state.loadedAnnualId === annualId;
    
    // Check if data is already fully loaded (but force reload if importToken is provided)
    if (!importToken && keyMatches && 
        state.bsTree && state.bsTree.length > 0 && 
        state.plTree && state.plTree.length > 0 && 
        Object.keys(state.bsValues || {}).length > 0 && 
        Object.keys(state.plValues || {}).length > 0 &&
        !state.loading) {
      // Data already loaded; ensure PL-PAT exists for BS validation gate
      if (state.plAfterTax === null || state.plAfterTax === undefined) {
        get().recomputePlAfterTax();
      }
      return;
    }
    
    // Prevent concurrent requests for the same key
    if (state.loading && keyMatches) {
      return;
    }
    
    set({ loading: true, error: null, plAfterTax: null, plAfterTaxLastYear: null });

    let localVersion = 0;
    set((s) => { localVersion = (s._reqVersion || 0) + 1; return { _reqVersion: localVersion }; });
    try {
      const detail = await getAnnualFs(companyId, annualId, importToken);
      // stale check
      let isStale = false;
      set((s) => { isStale = (s._reqVersion || 0) !== localVersion; return s; });
      if (isStale) return;

      const anyDetail: any = detail as any;
      // 新格式：直接从 balanceSheet.children 和 profitLoss.children 获取
      const bsLinesTree = anyDetail?.balanceSheet?.children as any[] | undefined;
      const plLinesTree = anyDetail?.profitLoss?.children as any[] | undefined;

      const toMapFromTree = (nodes: any[] | undefined | null): Record<number, string> => {
        const out: Record<number, string> = {};
        const walk = (arr: any[] | undefined | null) => {
          for (const it of arr || []) {
            const id = Number(it?.fsId ?? it?.id);
            const amt = it?.openingAmount ?? it?.amount ?? 0;
            if (!Number.isNaN(id)) out[id] = String(amt);
            if (Array.isArray(it?.children) && it.children.length > 0) walk(it.children);
          }
        };
        walk(nodes);
        return out;
      };
      
      const toLastYearMapFromTree = (nodes: any[] | undefined | null): { map: Record<number, string>; hasChangeAudited: Set<number> } => {
        const out: Record<number, string> = {};
        const hasChangeAudited = new Set<number>();
        const walk = (arr: any[] | undefined | null) => {
          for (const it of arr || []) {
            const id = Number(it?.fsId ?? it?.id);
            // Priority: lastYearChangeAudited > lastYearAudited
            // If lastYearChangeAudited exists and is not null, use it; otherwise use lastYearAudited
            const lastYearValue = it?.lastYearChangeAudited !== null && it?.lastYearChangeAudited !== undefined 
              ? it.lastYearChangeAudited 
              : (it?.lastYearAudited ?? 0);
            
            // Track if this node has lastYearChangeAudited from backend
            if (it?.lastYearChangeAudited !== null && it?.lastYearChangeAudited !== undefined) {
              hasChangeAudited.add(id);
            }
            
            if (!Number.isNaN(id)) out[id] = String(lastYearValue);
            if (Array.isArray(it?.children) && it.children.length > 0) walk(it.children);
          }
        };
        walk(nodes);
        return { map: out, hasChangeAudited };
      };
      
      const toPlValuesByYoaFromTree = (nodes: any[] | undefined | null): Record<string, Record<number, string>> => {
        const out: Record<string, Record<number, string>> = {};
        const walk = (arr: any[] | undefined | null) => {
          for (const it of arr || []) {
            const id = Number(it?.fsId ?? it?.id);
            const byYoa = it?.amountsByYearOfAssessment as Record<string, number> | undefined;
            if (!Number.isNaN(id) && byYoa && typeof byYoa === 'object') {
              for (const [yoa, amt] of Object.entries(byYoa)) {
                const key = yoa || '';
                if (!out[key]) out[key] = {};
                out[key][id] = String(amt ?? 0);
              }
            }
            if (Array.isArray(it?.children) && it.children.length > 0) walk(it.children);
          }
        };
        walk(nodes);
        return out;
      };

      const bsMap = toMapFromTree(bsLinesTree);
      const plMap = toMapFromTree(plLinesTree);
      const plByYoa = toPlValuesByYoaFromTree(plLinesTree);
      const bsLastYearResult = toLastYearMapFromTree(bsLinesTree);
      const plLastYearResult = toLastYearMapFromTree(plLinesTree);

      // Update byCode with setFsTree logic
      const next: Record<string, FsCategoryLine> = {};
      const upsert = (raw: any) => {
        const sc = String(raw?.scheduleCode ?? '').trim();
        const code = deriveLeafCode(raw);
        const fsIdNum = Number(raw?.fsId ?? 0) || 0;
        if (!code) {
          console.warn('[fsCategoryStore] Leaf node missing code; skipped indexing.', {
            fsId: raw?.fsId,
            nameEn: raw?.nameEn,
            name: raw?.name,
            scheduleCode: raw?.scheduleCode,
            level: raw?.level,
          });
          return;
        }
        const item: FsCategoryLine = {
          fsId: fsIdNum,
          name: raw?.name ?? null,
          nameEn: raw?.nameEn ?? null,
          aliasEn: raw?.aliasEn ?? null,
          nameZh: raw?.nameZh ?? null,
          aliasZh: raw?.aliasZh ?? null,
          statement: raw?.statement ?? '',
          amount: raw?.openingAmount ?? raw?.amount ?? null,
          finalAmount: raw?.finalAmount ?? null,
          scheduleName: raw?.scheduleName ?? null,
          code,
          scheduleCode: sc,
          entry: raw?.entry ?? null,  // 
          Level: raw?.Level ?? null,
          ...raw,
        } as FsCategoryLine;
        next[code] = item;
      };

      const traverse = (node: any) => {
        if (!node) return;
        const children = Array.isArray(node?.children) ? node.children : [];
        const lvl = node?.level;
        // level >= 3：常规叶子/父级科目；level 2 且无子节点：金额行（如 PL-TAX Taxation）
        if (Number(lvl) >= 3 || (Number(lvl) === 2 && children.length === 0)) {
          upsert(node);
        }
        for (const ch of children) traverse(ch);
      };

      const roots: any[] = [];
      if (Array.isArray(bsLinesTree)) roots.push(...bsLinesTree);
      if (Array.isArray(plLinesTree)) roots.push(...plLinesTree);
      for (const raw of roots) traverse(raw);

      const multiYoaKeys = Object.keys(plByYoa || {}).filter(
        (k) => k && Object.keys((plByYoa as any)[k] || {}).length > 0,
      );
      // Default: unchanged infer (SINGLE + MULTI with comparative LY / Cap Edit)
      let bsRetainedEarningsUseStored = inferRetainedEarningsUseStored(
        bsLinesTree,
        plLinesTree,
        bsMap,
        bsLastYearResult.map,
        plLastYearResult.map,
      );
      // MULTI + no AL/LFP/DIV comparative LY + derived≈0 → do not auto-Split on stale opening.
      // User Cap Edit still sets the flag later.
      if (multiYoaKeys.length >= 2 && bsRetainedEarningsUseStored) {
        const plLy = plAfterTaxLastYearFromMap(plLinesTree, plLastYearResult.map);
        const lyParts = retainedEarningsLastYearComponentsFromMap(
          (bsLinesTree || []) as any[],
          bsLastYearResult.map,
          plLy,
        );
        const hasComparativeLy = hasRetainedEarningsComparativeLastYear(lyParts);
        const derivedAl = computeDerivedRetainedEarnings((bsLinesTree || []) as any[], {
          bsLastYearMap: bsLastYearResult.map,
          plAfterTaxLastYear: plLy,
        });
        const storedAl = getRetainedProfitsStoredAmount(
          (bsLinesTree || []) as any[],
          bsMap,
        );
        if (!resolveRetainedEarningsAutoSplit(storedAl, derivedAl, hasComparativeLy)) {
          bsRetainedEarningsUseStored = false;
        }
      }
      const plCotInputMode = resolvePlCotInputMode(
        readStoredPlCotInputMode(companyId, annualId),
        {
          plTree: plLinesTree,
          bsTree: bsLinesTree,
          plValuesMap: plMap,
          bsValuesMap: bsMap,
          bsLastYearMap: bsLastYearResult.map,
        },
      );

      // Option 2: PL-PAT for BS validation without mounting ProfitLoss
      let plAfterTax = 0;
      let plAfterTaxLastYear = 0;
      try {
        const computed = computeFsPlAfterTaxFromMaps({
          plTree: plLinesTree,
          bsTree: bsLinesTree,
          plValues: plMap,
          plValuesByYoa: plByYoa,
          plLastYearValues: plLastYearResult.map,
          bsValues: bsMap,
          bsLastYearValues: bsLastYearResult.map,
          cotInputMode: plCotInputMode,
        });
        plAfterTax = computed.afterTax;
        plAfterTaxLastYear = computed.afterTaxLastYear;
      } catch {
      }

      set({
        loading: false,
        error: null,
        companyId,
        bsTree: (bsLinesTree || []) as any,
        plTree: (plLinesTree || []) as any,
        bsValues: bsMap,
        plValues: plMap,
        plValuesByYoa: plByYoa,
        bsLastYearValues: bsLastYearResult.map,
        plLastYearValues: plLastYearResult.map,
        bsHasLastYearChangeAudited: bsLastYearResult.hasChangeAudited,
        plHasLastYearChangeAudited: plLastYearResult.hasChangeAudited,
        byCode: next,
        firstYearAudit: anyDetail?.firstYearAudit || false,
        loadedCompanyId: companyId,
        loadedAnnualId: annualId,
        bsRetainedEarningsUseStored,
        plCotInputMode,
        plAfterTax,
        plAfterTaxLastYear,
      });
    } catch (e: any) {
      let isStale = false;
      set((s) => { isStale = (s._reqVersion || 0) !== localVersion; return s; });
      if (isStale) return;
      set({ loading: false, error: e?.message || 'Failed to load FS detail' });
    }
  },

  reset: () => set({ 
    byCode: {},
    bsTree: undefined,
    plTree: undefined,
    companyId: null,
    companyName: null,
    bsValues: {},
    plValues: {},
    plValuesByYoa: {},
    bsLastYearValues: {},
    plLastYearValues: {},
    bsHasLastYearChangeAudited: new Set(),
    plHasLastYearChangeAudited: new Set(),
    plAfterTax: null,
    plAfterTaxLastYear: null,
    plRoundedAfterTax: null,
    plPeriodType: null,
    afterTaxClientFromPL: null,
    plAdjustmentsByRefNo: {},
    adjustments: null,
    allowedScheduleCodes: undefined,
    scheduleNameMap: undefined,
    firstYearAudit: false,
    loading: false,
    error: null,
    _reqVersion: 0,
    loadedCompanyId: null,
    loadedAnnualId: null,
    bsRetainedEarningsUseStored: false,
    plCotInputMode: 'COS',
    glSuppressPlAfterTaxLastYear: false,
  }),

  applyImportResult: (result: ExcelImportResult) => {
    const fsData = result.fsData;
    const bsLinesTree = fsData?.balanceSheet?.children as any[] | undefined;
    const plLinesTree = fsData?.profitLoss?.children as any[] | undefined;

    const toMapFromTree = (nodes: any[] | undefined | null): Record<number, string> => {
      const out: Record<number, string> = {};
      const walk = (arr: any[] | undefined | null) => {
        for (const it of arr || []) {
          const id = Number(it?.fsId ?? it?.id);
          const amt = it?.openingAmount ?? it?.amount ?? 0;
          if (!Number.isNaN(id)) out[id] = String(amt);
          if (Array.isArray(it?.children) && it.children.length > 0) walk(it.children);
        }
      };
      walk(nodes);
      return out;
    };

    const toLastYearMapFromTree = (nodes: any[] | undefined | null): { map: Record<number, string>; hasChangeAudited: Set<number> } => {
      const out: Record<number, string> = {};
      const hasChangeAudited = new Set<number>();
      const walk = (arr: any[] | undefined | null) => {
        for (const it of arr || []) {
          const id = Number(it?.fsId ?? it?.id);
          const lastYearValue = it?.lastYearChangeAudited !== null && it?.lastYearChangeAudited !== undefined
            ? it.lastYearChangeAudited
            : (it?.lastYearAudited ?? 0);
          if (it?.lastYearChangeAudited !== null && it?.lastYearChangeAudited !== undefined) {
            hasChangeAudited.add(id);
          }
          if (!Number.isNaN(id)) out[id] = String(lastYearValue);
          if (Array.isArray(it?.children) && it.children.length > 0) walk(it.children);
        }
      };
      walk(nodes);
      return { map: out, hasChangeAudited };
    };

    const bsMap = toMapFromTree(bsLinesTree);
    const plMap = toMapFromTree(plLinesTree);
    const bsLastYear = toLastYearMapFromTree(bsLinesTree);
    const plLastYear = toLastYearMapFromTree(plLinesTree);

    const plCotInputMode = resolvePlCotInputMode(
      (() => {
        const companyId = get().loadedCompanyId;
        const annualId = get().loadedAnnualId;
        if (companyId && annualId) return readStoredPlCotInputMode(companyId, annualId);
        return null;
      })(),
      {
        plTree: plLinesTree,
        bsTree: bsLinesTree,
        plValuesMap: plMap,
        bsValuesMap: bsMap,
        bsLastYearMap: bsLastYear.map,
      },
    );

    set({
      bsValues: bsMap,
      plValues: plMap,
      bsLastYearValues: bsLastYear.map,
      plLastYearValues: plLastYear.map,
      bsHasLastYearChangeAudited: bsLastYear.hasChangeAudited,
      plHasLastYearChangeAudited: plLastYear.hasChangeAudited,
      bsTree: (bsLinesTree || []) as any,
      plTree: (plLinesTree || []) as any,
      bsRetainedEarningsUseStored: false,
      plCotInputMode,
    });
    get().recomputePlAfterTax();
  },

  patchLineAlias: (code, patch) => {
    const key = String(code || '').trim().toUpperCase();
    if (!key) return;

    const patchNode = (node: any): any => {
      if (!node) return node;
      const nodeCode = String(node.code || '').trim().toUpperCase();
      let next = node;
      if (nodeCode === key) {
        next = {
          ...node,
          ...(patch.name !== undefined ? {name: patch.name} : {}),
          ...(patch.aliasEn !== undefined ? {aliasEn: patch.aliasEn} : {}),
          ...(patch.aliasZh !== undefined ? {aliasZh: patch.aliasZh} : {}),
        };
      }
      if (Array.isArray(node.children) && node.children.length > 0) {
        const children = node.children.map(patchNode);
        if (children !== node.children || next !== node) {
          next = {...next, children};
        }
      }
      return next;
    };

    set((state) => {
      const prevLine = state.byCode[key];
      const byCode = prevLine
        ? {
            ...state.byCode,
            [key]: {
              ...prevLine,
              ...(patch.name !== undefined ? {name: patch.name} : {}),
              ...(patch.aliasEn !== undefined ? {aliasEn: patch.aliasEn} : {}),
              ...(patch.aliasZh !== undefined ? {aliasZh: patch.aliasZh} : {}),
            },
          }
        : state.byCode;
      return {
        byCode,
        bsTree: Array.isArray(state.bsTree) ? state.bsTree.map(patchNode) : state.bsTree,
        plTree: Array.isArray(state.plTree) ? state.plTree.map(patchNode) : state.plTree,
      };
    });
  },
}));